import React, { useEffect, useRef, useState } from 'react';
import { TooltipV2, TooltipV2Props } from '@withjoy/joykit';

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el?.parentElement) return null;
  const parent = el.parentElement;

  const { overflow, overflowY, overflowX } = getComputedStyle(parent);

  if (/(scroll|auto)/.test(overflow + overflowY + overflowX)) {
    return parent;
  }

  return findScrollParent(parent);
}

interface MaybeTooltipV2Props {
  readonly disabled: boolean;
  readonly children: JSX.Element;
  /** When true, closes the tooltip when the nearest scrollable ancestor scrolls */
  readonly closeOnScroll?: boolean;
}

export const TooltipV2Wrapper: React.FC<MaybeTooltipV2Props & TooltipV2Props> = props => {
  const { closeOnScroll = false, disabled, children, ...rest } = props;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!closeOnScroll || !triggerRef.current || disabled || !isOpen) return;

    const scrollParent = findScrollParent(triggerRef.current);

    if (!scrollParent) {
      return;
    }

    const handleScroll = () => setIsOpen(false);

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [closeOnScroll, disabled, isOpen]);

  if (disabled) {
    return children;
  }

  if (closeOnScroll) {
    return (
      <TooltipV2
        {...rest}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      >
        <div
          ref={triggerRef}
          style={{ display: 'inline-block' }}
        >
          {children}
        </div>
      </TooltipV2>
    );
  }

  return <TooltipV2 {...rest}>{children}</TooltipV2>;
};
